import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { authAPI } from '../services/api';
import { motion, AnimatePresence } from 'framer-motion';
import useCoachName from '../hooks/useCoachName';
import { useI18n, nextLang } from '../i18n';
import Seo from '../components/Seo';
import { track, trackNow } from '../utils/track';
import firebase from 'firebase/compat/app';
import 'firebase/compat/auth';

const Login = () => {
  const { t, lang, setLang } = useI18n(); // Extrair as variáveis de tradução
  const navigate = useNavigate();
  const { refresh: refreshCoachName } = useCoachName({ immediate: false });
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [rememberMe, setRememberMe] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [formState, setFormState] = useState('idle'); // idle, loading, success, error
  const [isGoogleLoading, setIsGoogleLoading] = useState(false);

  // Check for remembered email on component mount
  useEffect(() => {
    const rememberedEmail = localStorage.getItem('rememberedEmail');
    if (rememberedEmail) {
      setEmail(rememberedEmail);
      setRememberMe(true);
    }
  }, []);

  const handleGoogleSignIn = async () => {
    setError('');
    setIsGoogleLoading(true);
    try {
      const provider = new firebase.auth.GoogleAuthProvider();
      const result = await firebase.auth().signInWithPopup(provider);
      const idToken = await result.user.getIdToken();
      const res = await authAPI.googleAuth(idToken);
      trackNow((res?.isNewUser ?? res?.data?.isNewUser) ? 'signup_completed' : 'login_completed', { method: 'google', from: 'login' });
      await refreshCoachName().catch(() => {});
      navigate('/game');
    } catch (error) {
      if (error.code === 'auth/popup-closed-by-user') { setIsGoogleLoading(false); return; }
      track('login_error', { method: 'google', status: error.response?.status || 0 });
      setError(error.response?.data?.message || error.message || 'Google sign-in failed');
    } finally {
      setIsGoogleLoading(false);
    }
  };

  const handleLogin = async () => {
    // Reset any previous errors
    setError('');
    
    // Validate inputs
    if (!email) {
      setError('Email is required');
      return;
    }
    
    if (!password) {
      setError('Password is required');
      return;
    }
    
    setIsLoading(true);
    setFormState('loading');
    
    try {
      // Login with backend
      console.log('Starting login process...');
      const response = await authAPI.login(email, password);
      trackNow('login_completed', { method: 'email' });
      
      console.log('Login successful, response:', response);
      
      // Store token in local storage
      // The API returns the token directly in response.token
      if (response.token) {
        localStorage.setItem('token', response.token);
        console.log('Token saved:', response.token.substring(0, 20) + '...');
      } else {
        console.error('No token in response!');
      }
      
      // Store user data
      if (response.user) {
        localStorage.setItem('user', JSON.stringify(response.user));
        console.log('User data saved:', response.user);
      } else {
        console.error('No user data in response!');
      }
      
      // If remember me is checked, store email in local storage
      if (rememberMe) {
        localStorage.setItem('rememberedEmail', email);
      } else {
        localStorage.removeItem('rememberedEmail');
      }

      // Fetch and cache coach name
      try {
        await refreshCoachName();
        console.log('Coach name refreshed and cached.');
      } catch (e) {
        console.error('Failed to refresh coach name after login:', e);
        // Non-critical, so we don't block the login flow
      }
      
      setFormState('success');
      
      console.log('Redirecting to /play in 800ms...');
      
      // Small delay for success animation
      setTimeout(() => {
        // Redirect to game
        console.log('Navigating to /play now');
        navigate('/game');
      }, 800);
      
    } catch (error) {
      console.error('Login error:', error);
      track('login_error', { method: 'email', status: error.response?.status || 0 });
      setError(
        error.response?.data?.errors?.[0]?.msg || 
        error.response?.data?.message || 
        'Login failed. Please check your credentials and try again.'
      );
      setFormState('error');
    } finally {
      setIsLoading(false);
    }
  };

  // Handle Enter key press
  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleLogin();
    }
  };

  // Reset form state after error animation completes
  useEffect(() => {
    if (formState === 'error') {
      const timer = setTimeout(() => {
        setFormState('idle');
      }, 500);
      return () => clearTimeout(timer);
    }
  }, [formState]);

  return (
    <div className="relative min-h-screen w-full flex justify-center items-center overflow-hidden px-4 py-8" style={{ background: 'var(--surface-0)' }}>
      <Seo
        title="My Football Game Login – Back to Your Club"
        description="Log in to My Football Game to pick up your season, check your board and challenge your rivals. Sign in with email or Google."
        path="/login"
      />
      <div className="absolute top-6 right-6 z-50">
        <button
          onClick={() => setLang(nextLang(lang))}
          className="px-3 py-1.5 rounded text-[12px] font-bold tracking-wider border transition-all"
          style={{ background: 'var(--surface-1)', color: 'var(--text-2)', borderColor: 'var(--border-edge)' }}
          onMouseEnter={(e) => { e.currentTarget.style.color = 'var(--accent)'; e.currentTarget.style.borderColor = 'var(--accent)'; }}
          onMouseLeave={(e) => { e.currentTarget.style.color = 'var(--text-2)'; e.currentTarget.style.borderColor = 'var(--border-edge)'; }}
        >
          {nextLang(lang).toUpperCase()}
        </button>
      </div>

      {/* Main content */}
      <div className="relative z-10 w-full max-w-md">
        {/* Brand */}
        <div className="text-center mb-8">
          <a href="/" className="inline-block mb-3">
            <img alt="My Football Game" className="w-14 h-14 object-contain mx-auto" src="/logo.png" />
          </a>
          <div className="pc-eyebrow pc-eyebrow-accent mb-1">My Football Game · Access Terminal</div>
          <h1 className="pc-display text-5xl md:text-6xl text-[var(--text-1)]">
            {t('login.title')}
          </h1>
          <p className="text-[13px] text-[var(--text-3)] mt-2">
            {t('login.subtitle')}
          </p>
        </div>

        {/* Login form panel */}
        <motion.div
          className="relative rounded p-6 md:p-7 pc-banner pc-scan"
          style={{
            background: 'var(--surface-1)',
            border: '1px solid var(--border-edge)',
            boxShadow: '0 32px 64px -16px rgba(0,0,0,0.6)',
          }}
          initial={{ y: 16, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ delay: 0.15, duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
          variants={{ error: { x: [0, -8, 8, -8, 8, 0], transition: { duration: 0.4 } } }}
        >
          <div className="pc-eyebrow pc-eyebrow-accent mb-1">Authenticate</div>
          <h2 className="pc-display text-2xl text-[var(--text-1)] mb-5">WELCOME BACK</h2>

          {/* Google Sign-In */}
          <motion.button
            className="w-full py-3.5 px-5 rounded text-[14px] font-bold tracking-wide transition-all flex items-center justify-center gap-3 mb-4"
            style={{ background: '#ffffff', color: '#1f1f1f', fontFamily: 'var(--font-body)', border: '1px solid #dadce0' }}
            onClick={handleGoogleSignIn}
            disabled={isGoogleLoading}
            whileHover={{ y: -1 }}
            whileTap={{ y: 0 }}
          >
            <svg width="18" height="18" viewBox="0 0 48 48">
              <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"/>
              <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"/>
              <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"/>
              <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"/>
            </svg>
            {isGoogleLoading ? (t('login.signingIn')) : (t('login.continueWithGoogle'))}
          </motion.button>

          <div className="flex items-center gap-3 mb-4">
            <div className="flex-1 h-px" style={{ background: 'var(--border-edge)' }} />
            <span className="text-[11px] uppercase tracking-widest" style={{ color: 'var(--text-3)' }}>
              {t('login.orWithEmail')}
            </span>
            <div className="flex-1 h-px" style={{ background: 'var(--border-edge)' }} />
          </div>

          <AnimatePresence>
            {error && (
              <motion.div
                className="mb-4 px-3 py-2 rounded text-[12px]"
                style={{ background: 'rgba(248,113,113,0.08)', border: '1px solid rgba(248,113,113,0.30)', color: 'var(--loss)' }}
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: 'auto' }}
                exit={{ opacity: 0, height: 0 }}
                transition={{ duration: 0.25 }}
              >
                {error}
              </motion.div>
            )}
          </AnimatePresence>

          {/* Email */}
          <div className="relative mb-3">
            <div className="pc-eyebrow mb-1.5">{t('login.email')}</div>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              onKeyPress={handleKeyPress}
              placeholder="coach@club.com"
              className="w-full py-2.5 px-3 rounded text-[14px] outline-none transition-all"
              style={{
                background: 'var(--surface-2)',
                border: '1px solid var(--border-edge)',
                color: 'var(--text-1)',
                fontFamily: 'var(--font-mono)',
              }}
              onFocus={(e) => { e.currentTarget.style.borderColor = 'var(--accent)'; e.currentTarget.style.boxShadow = '0 0 0 1px var(--accent-dim)'; }}
              onBlur={(e) => { e.currentTarget.style.borderColor = 'var(--border-edge)'; e.currentTarget.style.boxShadow = 'none'; }}
            />
          </div>

          {/* Password */}
          <div className="relative mb-4">
            <div className="pc-eyebrow mb-1.5">{t('login.password')}</div>
            <input
              type={showPassword ? 'text' : 'password'}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              onKeyPress={handleKeyPress}
              placeholder="••••••••"
              className="w-full py-2.5 pl-3 pr-10 rounded text-[14px] outline-none transition-all"
              style={{
                background: 'var(--surface-2)',
                border: '1px solid var(--border-edge)',
                color: 'var(--text-1)',
                fontFamily: 'var(--font-mono)',
              }}
              onFocus={(e) => { e.currentTarget.style.borderColor = 'var(--accent)'; e.currentTarget.style.boxShadow = '0 0 0 1px var(--accent-dim)'; }}
              onBlur={(e) => { e.currentTarget.style.borderColor = 'var(--border-edge)'; e.currentTarget.style.boxShadow = 'none'; }}
            />
            <button
              type="button"
              className="absolute right-2 top-[34px] p-1.5 rounded hover:bg-white/5"
              onClick={() => setShowPassword(!showPassword)}
              style={{ color: 'var(--text-3)' }}
              title={showPassword ? 'Hide' : 'Show'}
            >
              {showPassword ? (
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" />
                </svg>
              ) : (
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                </svg>
              )}
            </button>
          </div>

          {/* Remember + Forgot */}
          <div className="flex items-center justify-between mb-5 text-[12px]">
            <label className="flex items-center cursor-pointer group" style={{ color: 'var(--text-2)' }}>
              <div
                className="w-4 h-4 rounded mr-2 flex items-center justify-center transition-colors"
                style={{
                  background: rememberMe ? 'var(--accent)' : 'var(--surface-2)',
                  border: `1px solid ${rememberMe ? 'var(--accent)' : 'var(--border-edge)'}`,
                }}
                onClick={() => setRememberMe(!rememberMe)}
              >
                <AnimatePresence>
                  {rememberMe && (
                    <motion.svg
                      className="h-3 w-3"
                      style={{ color: 'var(--surface-0)' }}
                      fill="none" viewBox="0 0 24 24" stroke="currentColor"
                      initial={{ scale: 0 }} animate={{ scale: 1 }} exit={{ scale: 0 }}
                      transition={{ type: 'spring', stiffness: 500, damping: 20 }}
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                    </motion.svg>
                  )}
                </AnimatePresence>
              </div>
              {t('login.remember')}
            </label>
            <span
              className="cursor-pointer transition-colors"
              style={{ color: 'var(--text-2)', fontFamily: 'var(--font-eyebrow)', letterSpacing: '0.15em', textTransform: 'uppercase' }}
              onClick={() => navigate('/forgot-password')}
              onMouseEnter={(e) => (e.currentTarget.style.color = 'var(--accent)')}
              onMouseLeave={(e) => (e.currentTarget.style.color = 'var(--text-2)')}
            >
              Forgot?
            </span>
          </div>

          {/* CTA */}
          <motion.button
            className="w-full py-3 px-5 rounded text-[13px] font-bold uppercase tracking-[0.2em] cursor-pointer text-center transition-all relative overflow-hidden disabled:opacity-60"
            style={{
              background: 'var(--accent)',
              color: 'var(--surface-0)',
              fontFamily: 'var(--font-body)',
              boxShadow: '0 0 24px var(--accent-glow)',
            }}
            onClick={handleLogin}
            disabled={isLoading}
            whileHover={{ y: -1 }}
            whileTap={{ y: 0 }}
          >
            <AnimatePresence mode="wait">
              {formState === 'loading' ? (
                <motion.span key="loading" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="inline-flex items-center justify-center gap-2">
                  <svg className="animate-spin w-4 h-4" viewBox="0 0 24 24" fill="none">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                  </svg>
                  {t("login.loading")}
                </motion.span>
              ) : formState === 'success' ? (
                <motion.span key="ok" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
                  ✓ Access granted
                </motion.span>
              ) : (
                <motion.span key="login" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
                  {t("login.submit")}
                </motion.span>
              )}
            </AnimatePresence>
          </motion.button>

          <div className="mt-5 text-center text-[12px]" style={{ color: 'var(--text-3)' }}>
            {t('login.noAccount')} <span className="cursor-pointer transition-colors" style={{ color: 'var(--accent)', fontWeight: 600 }} onClick={() => navigate('/register')}>
              {t('login.register')}
            </span>
          </div>
        </motion.div>

        <div className="mt-6 text-center pc-eyebrow">
          © 2026 My Football Game · All rights reserved
        </div>
      </div>
    </div>
  );
};

export default Login;